import React, { useEffect, useState } from "react";
import WordCard from "./WordCard";
import { Popover } from "@mui/material";

const TranslationViewer = ({ text, wordObjectsArray, dictionary, phrasalVerbs, phrasesDictionary }) => {
    const [formattedText, setFormattedText] = useState('');
    const [clickedWord, setClickedWord] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [lastHighlightedSpan, setLastHighlightedSpan] = useState(null);
    const [lastHighlightedPhrasalVerb, setLastHighlightedPhrasalVerb] = useState(null);
    const [mappingPositions, setMappingPositions] = useState([]);

    const open = Boolean(anchorEl);

    const buildText = () => {
        if (!text || !wordObjectsArray || wordObjectsArray.length === 0)
            return;

        let newText = text.slice(0, wordObjectsArray[0].positions.left);
        let arrLen = wordObjectsArray.length;
        
        // Store mapping positions in the same order as spans will be created
        setMappingPositions(wordObjectsArray.map(word => word.mapping_position.left));
        
        for (let i = 0; i < arrLen; ++i) {
            let wordObj = wordObjectsArray[i];
            let style = wordObj.translation ? 'background-color: #a5a5a5;' : '';
            newText += `<span class="charPosition" style="${style}">${wordObj.word}</span>`;

            if (i + 1 < arrLen) {
                let nextPos = wordObjectsArray[i + 1].positions;
                newText += text.slice(wordObj.positions.right, nextPos.left);
            }
        }
        newText += text.slice(wordObjectsArray[arrLen - 1].positions.right, text.length);

        setFormattedText(newText);
    };

    const findSpanByPosition = (position) => {
        const index = mappingPositions.indexOf(position.left);
        if (index === -1) return null;
        
        const spans = document.querySelectorAll('.charPosition');
        return spans[index];
    };

    const resetHighlights = () => {
        if (lastHighlightedSpan) {
            lastHighlightedSpan.style.textDecoration = 'none';
        }
        if (lastHighlightedPhrasalVerb) {
            lastHighlightedPhrasalVerb.forEach(span => {
                span.style.textDecoration = 'none';
            });
        }
    };

    const findPhrasalVerb = (clickedSpan) => {
        if (!phrasalVerbs || !clickedSpan) return null;

        // Get index of clicked span
        const spans = document.querySelectorAll('.charPosition');
        const index = Array.from(spans).indexOf(clickedSpan);
        if (index === -1) return null;

        // Get mapping position from index
        const mappingLeft = mappingPositions[index];

        const phraseObj = phrasalVerbs.find(pv => {
            // Check if any word in the phrasal verb matches the clicked position
            return pv.mapping_position.some(pos => pos && pos.left === mappingLeft);
        });

        if (phraseObj && phrasesDictionary) {
            // Get translation from phrasesDictionary using dictionaryPhraseId
            const dictEntry = phrasesDictionary.get(phraseObj.dictionaryPhraseId);
            return {
                ...phraseObj,
                ...dictEntry,
                phrase: dictEntry?.phrase || phraseObj.phrase || '',
                translation: dictEntry?.translation || ''
            };
        }

        return phraseObj;
    };

    const highlightPhrasalVerb = (phrasalVerb, startElement) => {
        resetHighlights();
        
        // Find and highlight all parts of the phrasal verb
        const phrasalVerbSpans = phrasalVerb.mapping_position
            .map(position => findSpanByPosition(position))
            .filter(span => span !== null); // Filter out any spans we couldn't find
        
        if (phrasalVerbSpans.length > 0) {
            phrasalVerbSpans.forEach(span => {
                span.style.textDecoration = 'underline';
            });
            setLastHighlightedPhrasalVerb(phrasalVerbSpans);
            setAnchorEl(startElement);
            return true;
        }
        console.log("No phrasal verb spans found");
        return false;
    };

    const handleCharacterClick = (e) => {
        resetHighlights();
        const clickedSpan = e.target.parentElement;

        // First check for phrasal verbs
        const phrasalVerb = findPhrasalVerb(clickedSpan);
        if (phrasalVerb) {
            const highlighted = highlightPhrasalVerb(phrasalVerb, clickedSpan);
            if (highlighted) {
                setClickedWord({
                    lemma: phrasalVerb.phrase,
                    translation: phrasalVerb.translation || '',
                    isPhrasalVerb: true
                });
                return;
            }
        }

        // If no phrasal verb found, proceed with regular word handling
        const spans = document.querySelectorAll('.charPosition');
        const index = Array.from(spans).indexOf(clickedSpan);
        
        if (index !== -1 && wordObjectsArray && wordObjectsArray[index]) {
            const wordObj = wordObjectsArray[index];
            let translation = wordObj.translation || '';

            if (dictionary && wordObj.dictionaryWordId) {
                const dictionaryEntry = dictionary.get(wordObj.dictionaryWordId);
                if (dictionaryEntry) {
                    translation = dictionaryEntry.translation || translation;
                }
            }

            // Highlight the whole word span
            clickedSpan.style.textDecoration = 'underline';
            setLastHighlightedSpan(clickedSpan);

            // Show popover with word info
            setClickedWord({
                ...wordObj,
                translation,
                isPhrasalVerb: false
            });
            setAnchorEl(clickedSpan);
        }
    };

    const handleClose = () => {
        resetHighlights();
        setAnchorEl(null);
        setLastHighlightedSpan(null);
        setLastHighlightedPhrasalVerb(null);
    };

    useEffect(() => {
        buildText();
    }, [text, wordObjectsArray]);

    useEffect(() => {
        if (formattedText) {
            document.querySelectorAll('.charPosition').forEach(el => {
                let characters = el.innerText.split('');
                el.innerHTML = '';
                characters.forEach(char => {
                    let span = document.createElement('span');
                    span.innerText = char;
                    span.addEventListener('click', handleCharacterClick);
                    el.appendChild(span);
                });
            });
        }
    }, [formattedText]);

    return (
        <>
            {formattedText.length > 0 && 
                <div 
                    id="text" 
                    dangerouslySetInnerHTML={{ __html: formattedText }}
                />
            }
            
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                {clickedWord && <WordCard word={clickedWord} />}
            </Popover>
        </>
    );
};

export default TranslationViewer;